import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { agentApi, chatApi } from '../services/api';
import { AgentDetail, ChatMessage } from '../types';
import { useLoading } from '../context/LoadingContext';
import { useNotification } from '../context/NotificationContext';
import { useErrorHandler } from '../hooks/useErrorHandler';

const AgentDetails = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const [agent, setAgent] = useState<AgentDetail | null>(null);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { startLoading, stopLoading } = useLoading();
  const { showNotification } = useNotification();
  const { handleError } = useErrorHandler();
  const navigate = useNavigate();

  const [isLoadingAgent, setIsLoadingAgent] = useState(false);
  const [hasAgentError, setHasAgentError] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [hasChatError, setHasChatError] = useState(false);

  const fetchAgentDetails = useCallback(async (id: string) => {
    // Prevent multiple simultaneous requests
    if (isLoadingAgent) return;
    
    try {
      setIsLoadingAgent(true);
      startLoading();
      const data = await agentApi.getAgentDetails(id);
      setAgent(data);
      setHasAgentError(false);
    } catch (error) {
      handleError(error);
      setHasAgentError(true);
      console.error('Failed to fetch agent details:', error);
    } finally {
      setIsLoadingAgent(false);
      stopLoading();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleError]);

  const fetchChatHistory = useCallback(async (id: string) => {
    // Prevent multiple simultaneous requests
    if (isLoadingChat) return;
    
    try {
      setIsLoadingChat(true);
      startLoading();
      const data = await chatApi.getChatHistory(id);
      setChatHistory(data.messages);
      setHasChatError(false);
    } catch (error) {
      handleError(error);
      setHasChatError(true);
      console.error('Failed to fetch chat history:', error);
    } finally {
      setIsLoadingChat(false);
      stopLoading();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleError]);

  useEffect(() => {
    if (!agentId) return;

    // Only fetch if we haven't had an error yet
    if (!hasAgentError) {
      fetchAgentDetails(agentId);
    }
    
    if (!hasChatError) {
      fetchChatHistory(agentId);
    }
  }, [agentId, fetchAgentDetails, fetchChatHistory, hasAgentError, hasChatError]);
  
  // Add retry handlers
  const handleRetryAgentDetails = () => {
    if (agentId) {
      setHasAgentError(false);
      fetchAgentDetails(agentId);
    }
  };
  
  const handleRetryChatHistory = () => {
    if (agentId) {
      setHasChatError(false);
      fetchChatHistory(agentId);
    }
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteAgent = async () => {
    if (!agentId) return;

    try {
      startLoading();
      await agentApi.deleteAgent(agentId);
      showNotification(`Agent "${agent?.name}" deleted successfully`, 'success');
      navigate('/agents');
    } catch (error) {
      handleError(error);
    } finally {
      stopLoading();
      handleCloseDeleteDialog();
    }
  };

  if (hasAgentError) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h5" color="error" gutterBottom>
          Failed to load agent details
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          There might be a connection issue with the backend server.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRetryAgentDetails}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/agents')}
          sx={{ mt: 2, ml: 2 }}
        >
          Back to Agents
        </Button>
      </Box>
    );
  }
  
  if (!agent) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h5">Loading agent details...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {agent.name}
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to={`/chat/${agentId}`}
            sx={{ mr: 2 }}
          >
            Chat with Agent
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleOpenDeleteDialog}
          >
            Delete Agent
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Agent Information
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1">
                <strong>ID:</strong> {agent.id}
              </Typography>
              <Typography variant="body1">
                <strong>Model:</strong> {agent.model}
              </Typography>
              <Typography variant="body1">
                <strong>Created:</strong> {new Date(agent.createdAt).toLocaleString()}
              </Typography>
              {/* End Conditions Section */}
              <Typography variant="body1" sx={{ mt: 2 }}>
                <strong>End Conditions:</strong>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {agent.endConditions && agent.endConditions.length > 0 ? (
                  agent.endConditions.map((condition) => (
                    <Chip key={condition.id} label={condition.description} size="small" />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No end conditions defined
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              System Prompt
            </Typography>
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
                whiteSpace: 'pre-wrap',
              }}
            >
              {agent.systemPrompt}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Chat History
            </Typography>
            <Divider />
            {hasChatError ? (
              <Box sx={{ textAlign: 'center', my: 3 }}>
                <Typography variant="body1" color="error" gutterBottom>
                  Failed to load chat history.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  onClick={handleRetryChatHistory}
                  sx={{ mt: 1 }}
                >
                  Retry
                </Button>
              </Box>
            ) : chatHistory.length === 0 ? (
              <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
                No chat history yet. Start a conversation with this agent.
              </Typography>
            ) : (
              <List>
                {chatHistory.slice(-5).map((message) => (
                  <React.Fragment key={message.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemText
                        primary={message.role === 'user' ? 'You' : 'Agent'}
                        secondary={
                          <>
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.primary"
                              sx={{ display: 'inline', whiteSpace: 'pre-wrap' }}
                            >
                              {message.content}
                            </Typography>
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ display: 'block', mt: 1 }}
                            >
                              {new Date(message.timestamp || (message.createdAt || '')).toLocaleString()}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="outlined"
                component={Link}
                to={`/chat/${agentId}`}
              >
                View Full Conversation
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Delete Agent</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the agent "{agent.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteAgent} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentDetails;
